import Scrollspy from 'react-scrollspy-ez';
import styled from 'styled-components';

import BadgeStyled from '../../../components/Badge';
import ContentHeaderStyled from '../../../components/ContentHeader';
import ExternalLinkGroupStyled from '../../../components/ExternalLinkGroup';
import NewTabNote from '../../../components/NewTabNote';
import NotificationStyled from '../../../components/Notification';
import PaginationStyled from '../../../components/Pagination';
import { LOGO_PACKAGE_LINKS, REFERENCE_LINKS } from '../../../constants';
import {
  Button,
  IconDownload as Download,
  IconDanger,
  IconInfoCircle as InfoCircle,
} from '../../../core';
import { useDownloadFile } from '../../../hooks/useDownloadFile';
import { ReactComponent as IconAccessibility } from '../../../images/icons/accessibility-56.svg';
import commonPageContentStyles, {
  textStyle,
} from '../../../styles/mixins/_commonPageContentStyles';
import navigationStyles from '../../../styles/mixins/navigationMenuStyles';

interface Props {
  className?: string;
}

const IMAGES = '/images/branding-styleguide-logos';

const dataExternalLinkGroups = [
  {
    id: 1,
    title: 'Government Usability Testing Resources',
    cards: [
      {
        title: 'The Section 508 accessibility program',
        subtitle: '[section508.gov]',
        linkUrl: REFERENCE_LINKS.section508Program,
      },
      {
        title: 'Accessibility for Teams guide',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovAccessibilityForTeams,
      },
      {
        title: 'Getting started with usability testing',
        subtitle: '[digital.gov]',
        linkUrl: REFERENCE_LINKS.digitalGovUsabilityTesting,
      },
    ],
  },
];

// Every example is an @2x WebP exported from the frame; width and height are the CSS size.
interface Example {
  image: string;
  width: number;
  height: number;
  alt: string;
  title?: string;
  text: string;
}

const mainVariations: Example[] = [
  {
    image: 'main-variation-department',
    width: 488,
    height: 300,
    alt: '',
    title: 'Official logo',
    text: 'Use this logo when representing the Department in all official communications to stakeholders, providers, partners, and DHCS employees.',
  },
  {
    image: 'main-variation-program',
    width: 488,
    height: 300,
    alt: '',
    title: 'Programs and services logo',
    text: 'Program logos are used when communicating outside the Department, directly with the public, about a specific program or service. The Program logo comprises the Poppy graphic + Program Name. The Office of Communications Design Team creates these logos as needed. Current program logos include (illustrative, not exhaustive): Medi-Cal, California Children’s Services, Medi-Cal Rx, Hearing Aid Coverage for Children Program, Medi-Cal for Kids & Teens, and California Partnership for Long-Term Care.',
  },
];

const secondaryLogos: Example[] = [
  {
    image: 'secondary-logo-full-color',
    width: 488,
    height: 300,
    alt: '',
    title: 'Grayscale',
    text: 'Use the grayscale lockup when full-colour reproduction isn’t available. It works on both light backgrounds (dark mark) and dark backgrounds (reversed).',
  },
  {
    image: 'secondary-logo-white',
    width: 488,
    height: 300,
    alt: '',
    title: 'Black & White',
    text: 'Use the solid black mark on light backgrounds and the solid white mark on dark or coloured backgrounds, where a one-colour logo is required.',
  },
];

const specialUseLogos: Example[] = [
  {
    image: 'special-use-logo-full-color',
    width: 488,
    height: 240,
    alt: '',
    title: 'Full-Color',
    text: 'Use on dark backgrounds.',
  },
  {
    image: 'special-use-logo-white',
    width: 488,
    height: 240,
    alt: '',
    title: 'White + Color',
    text: 'Use on dark backgrounds if there is enough contrast.',
  },
];

const officialLogos: Example[] = [
  {
    image: 'official-logo-full-color',
    width: 317,
    height: 180,
    alt: 'DHCS logo in full color on a light background',
    title: 'Full-Color',
    text: 'Use on light backgrounds. Primary version for all official communications.',
  },
  {
    image: 'official-logo-white',
    width: 317,
    height: 180,
    alt: 'DHCS logo in white on a DHCS Blue background',
    title: 'White',
    text: 'Use on dark backgrounds. The entire lockup renders white for maximum contrast.',
  },
  {
    image: 'official-logo-black',
    width: 317,
    height: 180,
    alt: 'DHCS logo in black on a light background',
    title: 'Black',
    text: 'Use on light backgrounds where color printing is unavailable.',
  },
];

const departmentMisuse = [
  'Do NOT stretch or compress the logo.',
  'Do NOT tweak the logo.',
  'Do NOT rearrange the logo.',
  'Do NOT change the font in the logo.',
  'Do NOT use the single “D” on its own.',
  'Do NOT place the logo on a background.',
  'Do NOT add outlines to the logo.',
  'Do NOT change the logo colors to unapproved colors.',
  'Do NOT add shadows or glows to the logo.',
].map((text, index) => ({ image: `misuse-0${index + 1}`, text }));

const departmentSizes: Example[] = [
  {
    image: 'clear-space-department',
    width: 488,
    height: 300,
    alt: 'The DHCS logos with the clear space marked: half the height of the D around the Department logo',
    title: 'Clear Space',
    text: 'As a minimum, keep clear space equal to half the height of the “D” around the Department logo, and half the width of the poppy around Program logos. DHCS logos should never be crowded by other items. Follow these minimum spacing rules to support the integrity, clarity, and legibility of the logos.',
  },
  {
    image: 'minimum-sizes-department',
    width: 488,
    height: 300,
    alt: 'The primary logo at a minimum width of 1 inch and the secondary logo at 0.75 inch',
    title: 'Minimum Sizes',
    text: 'The DHCS logos have been designed to perform well across all reproduction methods, from printed brochures to digital touchpoints.',
  },
];

const programSizes: Example[] = [
  {
    image: 'clear-space-program',
    width: 488,
    height: 300,
    alt: 'The Program logo with the clear space marked: half the size of the poppy',
    title: 'Clear space',
    text: 'Program logos should never be crowded by other items. Follow these minimum spacing rules to support the integrity, clarity, and legibility of the logos.',
  },
  {
    image: 'minimum-sizes-program',
    width: 488,
    height: 300,
    alt: 'The horizontal Program logo at a minimum width of 0.5 inch and the vertical logo at 0.7 inch',
    title: 'Minimum Sizes',
    text: 'The Program logos have been designed to perform well across all reproduction methods, from printed brochures to digital touchpoints.',
  },
];

const programMisuse: Example[] = [
  {
    image: 'misuse-program-01',
    width: 488,
    height: 300,
    alt: 'Program logos stretched, rotated, rearranged, outlined or recolored',
    text: 'Do not stretch or compress the logo, rotate or rearrange its elements, add outlines, or change the logo colors or font to anything unapproved.',
  },
  {
    image: 'misuse-program-02',
    width: 488,
    height: 300,
    alt: 'Program logos with a shadow, below the minimum size, the poppy on its own and on a photographic background',
    text: 'Do not add shadows or glows, scale the logo below its minimum size, use the poppy graphic on its own, or place the logo on a photographic background.',
  },
];

const hierarchyExamples: (Example & { wide?: boolean })[] = [
  {
    image: 'hierarchy-flyer',
    width: 488,
    height: 340,
    alt: '',
    title: 'Flyer',
    text: 'The DHCS logo is placed in a secondary position at the bottom of the page.',
  },
  {
    image: 'hierarchy-letterhead',
    width: 488,
    height: 340,
    alt: '',
    title: 'Letterhead',
    text: '“California Department of Health Care Services” is typeset in the footer.',
  },
  {
    image: 'hierarchy-brochure',
    width: 1000,
    height: 340,
    alt: '',
    title: 'Three-Panel Brochure',
    text: 'The DHCS logo appears in a secondary position on the back panel.',
    wide: true,
  },
];

// The print values are shown as text, hex included.
const swatches = [
  {
    id: 'yellow',
    name: 'DHCS Yellow',
    // tokens-allow: brand value shown as text
    values: 'HEX #F9A71C · RGB 249.167.28 · CMYK 0.39.99.0 · PMS 137C',
  },
  {
    id: 'blue',
    name: 'DHCS Blue',
    // tokens-allow: brand value shown as text
    values: 'HEX #17315A · RGB 23.49.90 · CMYK 100.86.37.30 · PMS 648C',
  },
  {
    id: 'black',
    name: 'Black',
    // tokens-allow: brand value shown as text
    values: 'HEX #000000 · RGB 0.0.0 · CMYK 0.0.0.100',
  },
  {
    id: 'white',
    name: 'White',
    // tokens-allow: brand value shown as text
    values: 'HEX #FFFFFF · RGB 255.255.255 · CMYK 0.0.0.0',
  },
];

const partnershipRows = [
  {
    heading: 'Logo Usage',
    cells: [
      'Written approval required. Don’t use the DHCS or program logo in a way that implies ownership or endorsement.',
      'Written approval required.',
      'Written approval from the DHCS program that issued the license is required.',
      'Written approval required.',
    ],
  },
  {
    heading: 'Required Disclaimer',
    cells: [
      '[Organization serves participants of DHCS-administered programs and acts independently — not an officer, employee, or agent of DHCS or the State of California.',
      'Funded in part by DHCS. Content is [Organization Name]’s responsibility and doesn’t necessarily represent the views of DHCS or the State.',
      '[Organization Name] is licensed by DHCS to provide [type of service] services. License #: [Insert] · Expires: [Insert Date]. Operates independently — not an officer, employee, or agent of DHCS or the State, and not approved or endorsed by any government agency.',
      'Funded by [Name of the Grant]. [Grantee] acts independently — not an officer, employee, or agent of DHCS or the State of California.',
    ],
  },
  {
    heading: 'Logo Placement',
    cells: [
      'Header/footer placement discouraged unless co-branded.',
      'Text-only footer acknowledgment permitted. Footer logo discouraged (needs approval + disclaimer). On websites, allowed only on About, Partners, or Acknowledgments pages.',
      'Disclaimer must be adjacent to the logo and prominently displayed. Not in footer or small print.',
      'Header/footer placement discouraged unless co-branded.',
    ],
  },
];

const partnershipColumns = [
  {
    title: 'Approved Partners and Providers',
    note: '(Entities that have an agreement with DHCS to provide services to program participants)',
  },
  { title: 'Funded Projects', note: '(Projects that are not directly managed by DHCS)' },
  { title: 'DHCS-licensed Entities', note: '(Entities that are licensed by DHCS)' },
  { title: 'Grantee', note: '(Entities that has been awarded grant funding by DHCS)' },
];

// The Contents entries are the text of the element that carries the id (react-scrollspy-ez reads
// its innerText). The frame's entries are shorter than the headings, so a zero-height anchor
// carries the short label and the heading itself has no id.
const NavAnchor = ({ id, label }: { id: string; label: string }) => (
  <span id={id} className="nav-anchor" aria-hidden="true">
    {label}
  </span>
);

const renderExamples = (examples: (Example & { wide?: boolean })[], className = '') => (
  <div className={`examples ${className}`.trim()}>
    {examples.map(({ image, width, height, alt, title, text, wide }) => (
      <figure className={wide ? 'example wide' : 'example'} key={image}>
        <img
          src={`${IMAGES}/${image}@2x.webp`}
          width={width}
          height={height}
          alt={alt}
          className="img-responsive"
        />
        <figcaption>
          {title && <strong>{title}</strong>}
          <p>{text}</p>
        </figcaption>
      </figure>
    ))}
  </div>
);

const renderMisuseCards = (cards: { image: string; text: string }[]) => (
  <div className="ex-cards misuse-cards">
    {cards.map(({ image, text }) => (
      <div className="ex-card false" key={image}>
        <img
          className="ex-card-img img-responsive"
          src={`${IMAGES}/${image}@2x.webp`}
          width={317}
          height={128}
          alt=""
        />
        <div className="ex-card-desc">
          <BadgeStyled className="danger" iconLeft={<IconDanger />}>
            Don&apos;t
          </BadgeStyled>
          <p>{text}</p>
        </div>
      </div>
    ))}
  </div>
);

const infoMessage = (title: React.ReactNode) => (
  <NotificationStyled icon={<InfoCircle />} title={title} color="blue" className="info-message" />
);

const LOGO_PACKAGES = [
  { label: 'SVG', path: LOGO_PACKAGE_LINKS.svg },
  { label: 'PNG', path: LOGO_PACKAGE_LINKS.png },
  { label: 'JPG', path: LOGO_PACKAGE_LINKS.jpg },
];

const LogoPackageButtons = () => {
  const downloadFile = useDownloadFile();

  return (
    <div className="download-assets-buttons">
      {LOGO_PACKAGES.map(({ label, path }) => (
        <Button
          key={label}
          size="M"
          iconAfter={<Download />}
          onClick={() => downloadFile(path.slice(path.lastIndexOf('/') + 1), path)}
        >
          {label}
        </Button>
      ))}
    </div>
  );
};

const LogosStructure = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="content">
      <ContentHeaderStyled
        title="Logos"
        breadcrumbs={[
          { label: 'Branding Styleguide', url: '/branding-styleguide' },
          { label: 'Logos' },
        ]}
      />
      <div className="page-container">
        <div className="page-content">
          <div className="page-content-with-navigation-wrapper">
            <div className="page-content-with-navigation">
              <section className="block">
                <h2 className="h1" id="overview">
                  Overview
                </h2>
                <p>
                  The Department of Health Care Services (DHCS) communicates to the public about its
                  various programs and services. Whether in print or online, all materials should
                  align with the design guidelines outlined in this document.
                </p>
                {infoMessage(
                  'Are you looking to download logos? Find them in Download Assets below.',
                )}
                <h3 className="h3" id="main-variations">
                  Main variations
                </h3>
                <p>
                  DHCS has two distinct logo types. Please refer to this page to understand when to
                  use each.
                </p>
              </section>
              {renderExamples(mainVariations)}
              <section className="block divider">
                <h2 className="h1" id="specifications">
                  Specifications
                </h2>
                <h2 className="h1" id="department-logo">
                  Department Logo
                </h2>
                <p>
                  This is the official logo for the Department of Health Care Services. Use this
                  logo when representing the Department in all official communications. Clarity is
                  key to the DHCS brand and using the logo on the appropriate background will allow
                  the logo to be showcased in the best way. Do not use the logo more than once per
                  page in your work.
                </p>
              </section>
              {infoMessage(
                'Do not use the logo more than once per page in your work. Download the logos from the Visual Identity Resources page.',
              )}
              <section className="block">
                <NavAnchor id="secondary-logo" label="Secondary Logo" />
                <h3 className="sub-title">Secondary Department Logo</h3>
                <p>
                  The primary DHCS logo should always be used. In certain instances, the secondary
                  logo may be used instead, if there is insufficient space for the primary logo, or
                  if the DHCS logo is being used in a place that is already branded DHCS.
                </p>
              </section>
              {renderExamples(secondaryLogos)}
              <section className="block">
                <NavAnchor id="special-logo" label="Special Logo" />
                <h3 className="sub-title">Special-Use Logo for Social Media</h3>
                <p>
                  This configuration is appropriate only for use on Social Media platforms and only
                  by the Design Team. Any other use is not permitted.
                </p>
              </section>
              {renderExamples(specialUseLogos)}
              {infoMessage(
                'Do NOT place the logo on a photographic background. These logos are pre-approved for use. If you need to use the logo in a different way or context, email the Design Team.',
              )}
              <section className="block">
                <NavAnchor id="official-logo" label="Official Logo" />
                <h3 className="h3">Official Department Logo</h3>
                <p>
                  Clarity is key to the DHCS brand — using the logo on the appropriate background
                  showcases it best. The lockup is shown here as vector art. Do not use the logo
                  more than once per page.
                </p>
              </section>
              {renderExamples(officialLogos, 'three')}
              <section className="block">
                <NavAnchor id="misuse" label="Misuse" />
                <h3 className="h3">Examples of Misuse</h3>
                <p>
                  Do not modify the DHCS logo or the state seal. The only modifications allowed are
                  the ones outlined in this guide. Use approved designs to maintain consistency and
                  to protect the integrity of the DHCS identity.
                </p>
              </section>
              {renderMisuseCards(departmentMisuse)}
              <section className="block">
                <NavAnchor id="space-sizes" label="Space & Sizes" />
                <h3 className="h3">Clear Space and Minimum Sizes</h3>
                <p>
                  The DHCS logos are designed to perform well across all reproduction methods, from
                  printed brochures to digital touchpoints. To ensure clarity and legibility, adhere
                  to these recommended minimum sizes: 1 inch wide for the primary (stacked) logo and
                  0.75 inch wide for the secondary logo.
                </p>
              </section>
              {renderExamples(departmentSizes)}
              <section className="block partnerships">
                <NavAnchor id="partnerships" label="Partnerships" />
                <h3 className="h3">Partnerships with External Organizations</h3>
                <div className="partnerships-details">
                  <div className="partnerships-text">
                    <p className="lead">
                      External organizations must follow logo usage guidelines. These rules protect
                      the integrity of the DHCS brand, ensure compliance with state law, and prevent
                      the public from thinking DHCS or the State of California directly operates,
                      endorses, or is affiliated with outside entities.
                    </p>
                    <div>
                      <p>
                        These guidelines apply to all external organizations that want to use the
                        DHCS logo, including:
                      </p>
                      <ul className="disc-list">
                        <li>Approved partners (e.g., contractors, grantees, vendors)</li>
                        <li>
                          Websites or projects funded by DHCS but not owned or operated by DHCS
                        </li>
                        <li>
                          Entities licensed by DHCS (e.g., health care facilities, behavioral health
                          providers)
                        </li>
                      </ul>
                    </div>
                    <h4>Consistency Across Programs</h4>
                    <p>
                      All DHCS programs must follow the same logo usage standards. If one program
                      allows a licensed entity or partner to use the DHCS logo, other similar
                      entities must be treated the same to ensure fairness and avoid confusion. The
                      licensing program is responsible for deciding if logo use is appropriate.
                      Written approval should only be given with program support and should not go
                      directly to the facility without that support.
                    </p>
                    <h4>Prohibited Without Context</h4>
                    <p>
                      Use of DHCS or Medi-Cal logos without the required disclaimer or without prior
                      written authorization is strictly prohibited and may violate Business and
                      Professions Code § 17533.6.
                    </p>
                  </div>
                  <aside className="steps">
                    <h4>Steps to Get Approval</h4>
                    <div>
                      <h4>1. DHCS Program submits request to the Office of Communications</h4>
                      <p>
                        Email <a href="mailto:designteam@dhcs.ca.gov">designteam@dhcs.ca.gov</a>{' '}
                        with:
                      </p>
                      <ul className="disc-list">
                        <li>Organization name</li>
                        <li>Contact information</li>
                        <li>Purpose of logo use</li>
                        <li>Draft artwork or mock-up showing logo placement and disclaimer</li>
                        <li>Confirmation of program support (for licensed organizations)</li>
                      </ul>
                    </div>
                    <div>
                      <h4>2. Wait for written approval</h4>
                      <p>
                        You must have written approval from the Office of Communications before
                        publishing or sharing any materials.
                      </p>
                    </div>
                    <div>
                      <h4>3. Ensure correct usage</h4>
                      <p>
                        Once approved, the program is responsible for making sure the logo and
                        disclaimer follow DHCS guidelines.
                      </p>
                    </div>
                  </aside>
                </div>
              </section>
              <section className="block">
                <p className="note">
                  The matrix below outlines the logo-usage requirements by organization type.
                </p>
                <table className="matrix">
                  <thead>
                    <tr>
                      <th scope="col">Type of External Organization</th>
                      {partnershipColumns.map(({ title, note }) => (
                        <th scope="col" key={title}>
                          <span className="matrix-title">{title}</span>
                          <span className="matrix-note">{note}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {partnershipRows.map(({ heading, cells }) => (
                      <tr key={heading}>
                        <th scope="row">{heading}</th>
                        {cells.map((cell, column) => (
                          // Two cells of a row can carry the same text, so the text is not a key.
                          <td key={partnershipColumns[column].title}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
              <section className="block">
                <NavAnchor id="co-branding" label="Co-branding" />
                <h3 className="h3">Co-branding</h3>
                <p>
                  When co-branding or co-authoring a piece of collateral, it’s important to maintain
                  the integrity of the DHCS brand by not blending colors, fonts, and styles with the
                  collaborator’s brand. If DHCS ultimately owns the piece, the branding should be
                  DHCS.
                </p>
                <ul className="disc-list co-branding-list">
                  <li>
                    Be sure to begin any job with a template found on the{' '}
                    <a
                      href={REFERENCE_LINKS.dhcsVisualIdentityResources}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Visual Identity Resources page
                      <NewTabNote />
                    </a>{' '}
                    to ensure it will meet brand and accessibility requirements.
                  </li>
                  <li>
                    Co-branded logos represent an equal partnership between two organizations.
                  </li>
                  <li>
                    If DHCS is the lead in the partnership, the DHCS logo should appear to the left
                    of the vertical line. If the partner is the lead, the DHCS logo would appear to
                    the right of the vertical line.
                  </li>
                  <li>
                    When sizing the logos, be sure they have equal prominence. Because partner logos
                    vary in shape, the logos should appear optically equal even if they’re not the
                    exact same size.
                  </li>
                </ul>
              </section>
              <img
                src={`${IMAGES}/co-branding-department@2x.webp`}
                alt="The DHCS logo to the left of a vertical line and an external organization logo to the right, with the spacing between them marked X"
                width={1000}
                height={400}
                className="img-responsive"
              />
              <section className="block divider program-logo">
                <h2 className="h1" id="program-logo">
                  Program Logo
                </h2>
                <p>
                  Program logos are used when communicating outside the Department, directly with
                  the public, about a specific Program or service.
                </p>
                <p>Audiences:</p>
                <ul className="disc-list">
                  <li>General Public</li>
                  <li>Members</li>
                  <li>People using our services</li>
                </ul>
                <p>Examples of use:</p>
                <ul className="disc-list">
                  <li>Program-specific presentations</li>
                  <li>Member-facing outreach materials</li>
                  <li>Member-facing websites</li>
                  <li>Marketing or informational content about</li>
                </ul>
                <p>
                  The Program logo comprises the Poppy graphic + Program Name. The Office of
                  Communications (OC) Design Team creates these logos as needed. Email the Design
                  Team to request one for your Program. Programs, projects, and initiatives are not
                  permitted to create their own logos. Use only the approved logos to maintain
                  consistency with the DHCS brand.
                </p>
              </section>
              <img
                src={`${IMAGES}/program-logos@2x.webp`}
                alt="Medi-Cal, California Children’s Services, Medi-Cal Rx and Hearing Aid Coverage for Children Program logos, each with the poppy graphic"
                width={1000}
                height={400}
                className="img-responsive"
              />
              <section className="block spaced">
                <NavAnchor id="program-official-logo" label="Official Logo" />
                <div className="text">
                  <h3 className="h3">Official Program Logo</h3>
                  <p>
                    The Program logo comprises the Poppy graphic plus the Program Name. The Office
                    of Communications (OC) Design Team creates these logos as needed — email the
                    Design Team to request one for your Program. Programs, projects, and initiatives
                    are not permitted to create their own logos. Use only the approved logos to
                    maintain consistency with the DHCS brand.
                  </p>
                </div>
                <img
                  src={`${IMAGES}/program-logo-official@2x.webp`}
                  alt="The Program logo in full color, white and color, white, white and orange center, and black, each on its approved background"
                  width={1000}
                  height={400}
                  className="img-responsive"
                />
              </section>
              <section className="block spaced">
                <NavAnchor id="program-vertical-logo" label="Vertical Logo" />
                <div className="text">
                  <h3 className="h3">Vertical Program Logo</h3>
                  <p>
                    If the program name is fewer than 10 characters, you may create a vertical
                    version of the logo. The logo may be used on a color background, but follow
                    these guidelines to ensure readability and recognition. The logos will need to
                    be modified in design software to work on different color backgrounds. Download
                    the logos from the Visual Identity Resources page, or email the Design Team for
                    assistance.
                  </p>
                </div>
                <img
                  src={`${IMAGES}/program-logo-vertical@2x.webp`}
                  alt="The vertical Program logo in full color, white and color, white, white and orange center, and black, each on its approved background"
                  width={1000}
                  height={400}
                  className="img-responsive"
                />
              </section>
              <section className="block">
                <NavAnchor id="program-space-sizes" label="Space & Sizes" />
                <h3 className="h3">Clear Space and Minimum Sizes</h3>
                <p>
                  The Program logos are designed to perform well across all reproduction methods,
                  from printed brochures to digital touchpoints. To ensure clarity and legibility,
                  adhere to these recommended minimum sizes: 0.5 inch for the horizontal logo and
                  0.7 inch for the vertical logo.
                </p>
              </section>
              {renderExamples(programSizes)}
              <section className="block divider">
                <NavAnchor id="program-misuse" label="Misuse" />
                <h3 className="h3">Examples of Misuse</h3>
                <p>
                  Do not modify the Program logo. The only modifications allowed are the ones
                  outlined in this guide. Use approved designs to maintain consistency and to
                  protect the integrity of the identity.
                </p>
              </section>
              {renderExamples(programMisuse)}
              <section className="block">
                <NavAnchor id="program-partnerships" label="Partnerships" />
                <h3 className="h3">Partnerships with External Organizations</h3>
                <p>
                  External organizations must follow logo usage guidelines. These rules protect the
                  integrity of Program brands, ensure compliance with state law, and prevent the
                  public from thinking DHCS or the State of California directly operates, endorses,
                  or is affiliated with external entities. This policy applies to approved partners
                  (contractors, grantees, vendors), projects funded by DHCS, and entities licensed
                  by DHCS.
                </p>
              </section>
              <figure className="example">
                <img
                  src={`${IMAGES}/partnership-program-example@2x.webp`}
                  width={1000}
                  height={280}
                  alt="The Program logo to the left of a vertical line and an external organization logo to the right, with the spacing between them marked X"
                  className="img-responsive"
                />
                <figcaption className="caption-columns">
                  <div>
                    <strong>Logo sizing</strong>
                    <p>
                      Co-branded logos represent an equal partnership. If the Program is the lead,
                      its logo appears to the left of the vertical line; if the partner is the lead,
                      to the right. Size the logos so they appear optically equal, even if they are
                      not the exact same size.
                    </p>
                  </div>
                  <div>
                    <strong>Co-branding</strong>
                    <p>
                      Maintain the integrity of the Program brand by not blending colors, fonts, and
                      styles with the collaborator&apos;s brand. If the Program ultimately owns the
                      piece, the branding should be the Program branding. Begin any job with a
                      template from the Visual Identity Resources page.
                    </p>
                  </div>
                </figcaption>
              </figure>
              <section className="block">
                <NavAnchor id="logo-hierarchy" label="Logo Hierarchy" />
                <h3 className="h3">Program and DHCS Logo Hierarchy</h3>
                <p>
                  The Program and DHCS logos should never be co-branded. All Program-branded
                  collateral should lead with the Program logo. The DHCS logo may be used to support
                  the Program, but in a secondary position — for example, placed in the footer at a
                  smaller size, or by typesetting &quot;California Department of Health Care
                  Services&quot; in the footer.
                </p>
              </section>
              {renderExamples(hierarchyExamples)}
              <section className="block spaced">
                <div className="text">
                  <h3 className="h3" id="color-palette">
                    Color Palette
                  </h3>
                  <p>
                    The Program logo uses two colors from our color palette — DHCS Blue and DHCS
                    Yellow. Along with black for body text, layout designs should mostly feature
                    these colors. Use the Program brand colors unless the template’s default colors
                    are a tint. Choose the color profile that fits your medium: HEX for web, RGB for
                    digital screens, CMYK for print, and Pantone (PMS) for consistent matching
                    across mediums.
                  </p>
                </div>
                <div className="notes">
                  {infoMessage(
                    'Printing your materials may affect the colors depending on your medium, the type of printer, and the ink.',
                  )}
                  {infoMessage(
                    'Reference the Data Publishing Style Guide for the data color palette if your document contains data.',
                  )}
                </div>
                <img
                  src={`${IMAGES}/color-palette-logo@2x.webp`}
                  alt="The Program logo with its two colors labelled: the DHCS Yellow poppy and the DHCS Blue name"
                  width={1000}
                  height={200}
                  className="img-responsive"
                />
                <div className="swatches">
                  {swatches.map(({ id, name, values }) => (
                    <div className="swatch" key={id}>
                      <span className={`swatch-bar ${id}`} />
                      <strong>{name}</strong>
                      <p>{values}</p>
                    </div>
                  ))}
                </div>
              </section>
              <section className="block divider references">
                <h2 className="h1" id="references">
                  References
                </h2>
                {dataExternalLinkGroups.map((externalLinkGroup) => (
                  <ExternalLinkGroupStyled
                    key={externalLinkGroup.id}
                    title={externalLinkGroup.title}
                    cards={externalLinkGroup.cards}
                  />
                ))}
                <div className="text">
                  <h3 className="h3" id="download-assets">
                    Download Assets
                  </h3>
                  <p>
                    All DHCS logos, including program logos, are available for download from the
                    Visual Identity Resources page. Use the pre-approved versions to stay on brand
                    and meet accessibility requirements.
                  </p>
                </div>
                <div className="download-assets">
                  <div className="download-assets-info">
                    <div className="download-assets-icon">
                      <IconAccessibility />
                    </div>
                    <div className="download-assets-title">
                      <strong>Logo Package</strong>
                      <p>
                        <span>8</span> Files
                      </p>
                    </div>
                  </div>
                  <LogoPackageButtons />
                </div>
              </section>
            </div>
            <div>
              <div className="page-navigation">
                <h2>Contents</h2>
                <Scrollspy
                  ids={[
                    'overview',
                    'main-variations',
                    'specifications',
                    'department-logo',
                    'secondary-logo',
                    'special-logo',
                    'official-logo',
                    'misuse',
                    'space-sizes',
                    'partnerships',
                    'co-branding',
                    'program-logo',
                    'program-official-logo',
                    'program-vertical-logo',
                    'program-space-sizes',
                    'program-misuse',
                    'program-partnerships',
                    'logo-hierarchy',
                    'color-palette',
                    'references',
                  ]}
                  offset={0}
                  activeItemClassName="active"
                  itemContainerClassName="multilevel-link-list"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <PaginationStyled
        previousLink="/branding-styleguide/typography"
        previous="Typography"
        nextLink="/branding-styleguide/icons"
        next="Iconography & Graphics"
      />
    </div>
  </div>
);

// The Updated frame stacks every block of the page on a 40px rhythm; the text inside a block is
// spaced 16px (24px under a section title).
const Logos = styled(LogosStructure)`
  ${commonPageContentStyles}
  ${navigationStyles([2, 5, 6, 7, 8, 9, 10, 11, 13, 14, 15, 16, 17, 18, 19])}
    overflow: unset;
  height: 100%;

  > .content {
    width: 100%;
    padding: 0;
  }

  .page-content .page-content-with-navigation {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    > * {
      margin: 0;
    }

    .block {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      max-width: 62.5rem;

      > p,
      > ul,
      > h2,
      > h3,
      > h4 {
        max-width: 43.75rem;
        margin: 0;
      }

      > .h1 {
        margin-bottom: 2.5rem;
      }

      &.spaced {
        gap: 2.5rem;
      }

      .info-message {
        margin: 0.5rem 0;
      }
    }

    .text {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      max-width: 43.75rem;

      > * {
        margin: 0;
      }
    }

    .nav-anchor {
      display: block;
      height: 0;
      overflow: hidden;
    }

    .sub-title {
      ${textStyle('h4Sb')}
      color: ${({ theme }) => theme.neutralTextWeak};
    }

    .info-message {
      display: flex;
      width: fit-content;
      max-width: 43.75rem;
    }

    .notes {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    .disc-list {
      ${textStyle('paragraphMedium')}
      margin: 0;
    }

    .examples {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1.5rem;

      &.three {
        grid-template-columns: repeat(3, minmax(0, 1fr));
      }
    }

    .example {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin: 0;

      &.wide {
        grid-column: 1 / -1;
      }

      img {
        display: block;
      }

      figcaption {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        letter-spacing: 0;
        color: ${({ theme }) => theme.neutralTextWeak};

        strong {
          ${textStyle('h4Sb')}
        }

        p {
          ${textStyle('paragraphMedium')}
          margin: 0;
        }

        &.caption-columns {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1.5rem;

          > div {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      }
    }

    .examples.three .example figcaption {
      gap: 0.125rem;
    }

    .misuse-cards {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      column-gap: 1.5rem;
      row-gap: 1.5rem;
      margin: 0;

      .ex-card-img {
        width: 100%;
        height: auto;
      }

      .ex-card-desc {
        min-height: 8.25rem;
        gap: 1rem;
      }
    }

    .partnerships-details {
      display: flex;
      gap: 2.5rem;
      align-items: flex-start;
      max-width: none;
    }

    .partnerships-text {
      display: flex;
      flex: 1;
      flex-direction: column;
      gap: 1rem;
      min-width: 0;

      p,
      h4 {
        margin: 0;
      }

      p:not(.lead) {
        ${textStyle('paragraphMedium')}
        margin-bottom: 0.5rem;
      }

      h4 {
        color: ${({ theme }) => theme.neutralTextWeak};
      }
    }

    .steps {
      display: flex;
      flex: none;
      flex-direction: column;
      gap: 1rem;
      width: 19.875rem;
      padding: 1.5rem;
      background: ${({ theme }) => theme.neutralBackground};
      border: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
      border-radius: 0.5rem;

      > div {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      h4,
      p {
        margin: 0;
        color: ${({ theme }) => theme.neutralTextWeak};
      }

      p {
        ${textStyle('paragraphMedium')}
      }

      a {
        text-decoration: underline;
      }
    }

    .note {
      ${textStyle('paragraphMedium')}
    }

    .matrix {
      width: 100%;
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: 0;
      border: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
      border-radius: 0.5rem;
      overflow: hidden;

      th,
      td {
        padding: 0.625rem 1rem;
        text-align: left;
        vertical-align: top;
        border-bottom: 0.0625rem solid ${({ theme }) => theme.neutralBorder};
      }

      tr:last-child > th,
      tr:last-child > td {
        border-bottom: 0;
      }

      th {
        ${textStyle('h4Sb')}
        color: ${({ theme }) => theme.neutralText};
      }

      thead th:first-child,
      tbody th {
        width: 8.75rem;
        background: ${({ theme }) => theme.neutralBackgroundMedium};
      }

      td {
        ${textStyle('paragraphSmall')}
        color: ${({ theme }) => theme.neutralTextWeak};
      }

      .matrix-title,
      .matrix-note {
        display: block;
      }

      .matrix-note {
        ${textStyle('body12')}
        margin-top: 0.375rem;
        color: ${({ theme }) => theme.neutralTextDisabled};
      }
    }

    .co-branding-list {
      max-width: 42.8125rem;
    }

    .program-logo > .h1,
    .references > .h1 {
      margin-bottom: 0;
    }

    .swatches {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 2.5rem 1.5rem;
      max-width: none;
    }

    .swatch {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      strong {
        ${textStyle('h4Sb')}
      }

      p {
        ${textStyle('paragraphMedium')}
        max-width: none;
        margin: 0;
      }
    }

    .swatch-bar {
      display: block;
      height: 1.25rem;
      border-radius: 0.25rem;

      &.yellow {
        background: var(--color-brand-yellow);
      }

      &.blue {
        background: var(--color-brand-blue);
      }

      &.black {
        background: var(--color-brand-black);
      }

      &.white {
        background: var(--color-brand-white);
        border: 0.0625rem solid ${({ theme }) => theme.neutralBorderMedium};
      }
    }

    .references {
      gap: 1.5rem;
    }
  }
`;

export default Logos;
